import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { formatScheduledDate } from "@/lib/dateUtils";
import { Calendar, ClipboardList, User, Clock, FileText, ExternalLink, MessageCircle, Camera, Pencil, DollarSign, Upload } from "lucide-react";
import { ImportTurnoverPhotosDialog } from "@/components/owner/ImportTurnoverPhotosDialog";
import { Link } from "react-router-dom";
import { MessageThreadModal } from "@/components/messaging/MessageThreadModal";
import { AccessInstructionsView } from "@/components/owner/AccessInstructionsView";
import { useCleaningSubmissions } from "@/hooks/useCleaningSubmissions";
import { usePropertyWithAccess } from "@/hooks/useProperties";
import { useTaskSubmissionPhotos, getPhotosForTask } from "@/hooks/useTaskSubmissionPhotos";
import { useTaskReferencePhotos, getPhotosForTask as getRefPhotosForTask } from "@/hooks/useTaskReferencePhotos";
import { useTaskChecklistAssignments } from "@/hooks/useTaskChecklistAssignments";
import { SubmissionPhotoGallery } from "./SubmissionPhotoGallery";
import CleanerRatingCard from "./CleanerRatingCard";
import { useUpdateTaskCleaningFee } from "@/hooks/useEditCleaningTask";
import { Input } from "@/components/ui/input";
import { ProofEvidenceSection } from "./ProofEvidenceSection";
import { ShareWithGuest } from "./ShareWithGuest";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface CleaningTask {
  id: string;
  property_id: string;
  reservation_id: string | null;
  scheduled_date: string;
  status: string;
  cleaner_id: string | null;
  checklist_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  duration_minutes: number | null;
  cleaning_fee?: number | null;
  assignment_reason: string | null;
  reservation?: {
    start_date: string;
    end_date: string;
    source: string | null;
  } | null;
  checklists?: {
    id: string;
    name: string;
  } | null;
}

interface TaskDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: CleaningTask | null;
  cleanerName: string | null;
  companyName: string | null;
  propertyId: string;
  /** When set, shows Edit Task button so user can edit date, time, checklist, assignee (same as elsewhere in app) */
  onEditTask?: (task: CleaningTask) => void;
}

export const TaskDetailDrawer = ({
  open,
  onOpenChange,
  task,
  cleanerName,
  companyName,
  propertyId,
  onEditTask,
}: TaskDetailDrawerProps) => {
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [importPhotosOpen, setImportPhotosOpen] = useState(false);
  const [feeEditing, setFeeEditing] = useState(false);
  const [feeEditValue, setFeeEditValue] = useState("");
  const [feeOverride, setFeeOverride] = useState<{ taskId: string; value: number | null } | null>(null);

  const updateCleaningFee = useUpdateTaskCleaningFee();
  const displayFee = feeOverride?.taskId === task?.id ? (feeOverride?.value ?? task?.cleaning_fee ?? null) : (task?.cleaning_fee ?? null);

  // Fetch submission for this task
  const { data: submissions } = useCleaningSubmissions(propertyId);
  const submission = submissions?.find(s => s.cleaning_task_id === task?.id);
  const { data: submissionPhotos } = useTaskSubmissionPhotos(submission?.id);
  const { data: referencePhotosData } = useTaskReferencePhotos(propertyId, task?.checklist_id ?? undefined);
  const referencePhotos = referencePhotosData?.list;
  const { data: property } = usePropertyWithAccess(open ? propertyId : undefined);
  const { data: checklistAssignments = [] } = useTaskChecklistAssignments(open && task ? task.id : undefined);
  const { data: liveProgress } = useQuery({
    queryKey: ["task-live-progress-owner", task?.id, task?.checklist_id, checklistAssignments.map((a) => a.checklist_id).join(","), open],
    enabled: !!task && open && task.status === "in_progress",
    queryFn: async (): Promise<{ completed: number; total: number } | null> => {
      if (!task) return null;

      const checklistIds = checklistAssignments.length > 0
        ? [...new Set(checklistAssignments.map((a) => a.checklist_id))]
        : task.checklist_id
          ? [task.checklist_id]
          : [];

      let totalTasks = 0;
      if (checklistIds.length > 0) {
        const { data: checklistRows, error: checklistError } = await supabase
          .from("checklists")
          .select("id, rooms")
          .in("id", checklistIds);
        if (!checklistError) {
          totalTasks = (checklistRows ?? []).reduce((sum, row) => {
            const rooms = Array.isArray(row.rooms) ? row.rooms : [];
            const checklistTaskCount = rooms.reduce((roomSum, room) => {
              const tasks = Array.isArray((room as { tasks?: unknown[] }).tasks) ? (room as { tasks?: unknown[] }).tasks : [];
              return roomSum + tasks.length;
            }, 0);
            return sum + checklistTaskCount;
          }, 0);
        }
      }

      const { data: progressRow, error: progressError } = await supabase
        .from("cleaning_progress_temp")
        .select("completed_tasks")
        .eq("cleaning_task_id", task.id)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (progressError) {
        return totalTasks > 0 ? { completed: 0, total: totalTasks } : null;
      }

      const completedTasks = Array.isArray(progressRow?.completed_tasks) ? progressRow.completed_tasks.length : 0;
      if (totalTasks <= 0 && completedTasks <= 0) return null;
      return {
        completed: completedTasks,
        total: totalTasks,
      };
    },
  });

  const accessInstructions = property
    ? {
        entry_method: property.entry_method ?? null,
        entry_code: property.entry_code ?? null,
        entry_location: property.entry_location ?? null,
        unit_location: property.unit_location ?? null,
        parking_instructions: property.parking_instructions ?? null,
        supply_closet_info: property.supply_closet_info ?? null,
        access_notes: property.access_notes ?? null,
      }
    : null;

  if (!task) return null;

  const cleaningTaskFromSubmission = submission?.cleaning_tasks as {
    id: string;
    rating: number | null;
    rating_note: string | null;
    rated_at: string | null;
    checklist_completed: boolean | null;
    inventory_completed: boolean | null;
  } | null | undefined;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Completed</Badge>;
      case "in_progress":
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">In Progress</Badge>;
      case "missed":
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Missed</Badge>;
      case "scheduled":
      default:
        return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">Scheduled</Badge>;
    }
  };

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return null;
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col h-full max-h-[100dvh] sm:max-w-[400px] p-0">
        <SheetHeader className="shrink-0 px-6 pt-6 pb-2">
          <SheetTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            Task Details
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 mt-2 space-y-6">
          {/* Access Instructions */}
          <AccessInstructionsView instructions={accessInstructions} defaultOpen={true} />

          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Status</span>
            {getStatusBadge(task.status)}
          </div>
          {task.status === "in_progress" && liveProgress && (
            <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
              <span className="text-sm text-muted-foreground">In progress: Tasks Done</span>
              <span className="text-sm font-medium">
                {liveProgress.completed}/{liveProgress.total > 0 ? liveProgress.total : "—"}
              </span>
            </div>
          )}

          {/* Scheduled Date */}
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              Scheduled Date
            </div>
            <p className="font-medium">
              {formatScheduledDate(task.scheduled_date, "MMMM d, yyyy")}
            </p>
          </div>

          {/* Checklists & assignees (multiple when present; no limit) */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ClipboardList className="w-4 h-4" />
              {checklistAssignments.length > 1 ? "Checklists" : "Checklist"}
            </div>
            {checklistAssignments.length > 0 ? (
              <ul className="space-y-2">
                {checklistAssignments.map((a) => (
                  <li key={a.checklist_id} className="flex flex-col gap-0.5 rounded-md border bg-muted/30 px-3 py-2 text-sm">
                    <span className="font-medium">{a.checklist_name}</span>
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <User className="w-3.5 h-3.5 shrink-0" />
                      {a.assignee_label}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="space-y-1">
                <p className="font-medium">
                  {task.checklists?.name ?? "No checklist assigned"}
                </p>
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <User className="w-4 h-4 shrink-0" />
                  {cleanerName || "Unassigned"}
                </p>
              </div>
            )}
          </div>

          {/* Duration (if completed) */}
          {task.status === "completed" && task.duration_minutes && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                Duration
              </div>
              <p className="font-medium">
                {formatDuration(task.duration_minutes)}
              </p>
            </div>
          )}

          {/* Completed At */}
          {task.status === "completed" && task.completed_at && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                Completed
              </div>
              <p className="font-medium">
                {format(new Date(task.completed_at), "MMM d, yyyy 'at' h:mm a")}
              </p>
            </div>
          )}

          {/* Cleaning fee (rate for owner to pay) - show when completed */}
          {task.status === "completed" && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <DollarSign className="w-4 h-4" />
                Cleaning fee (rate for payment)
              </div>
              {feeEditing ? (
                <div className="flex gap-2 items-center">
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    placeholder="e.g. 75"
                    value={feeEditValue}
                    onChange={(e) => setFeeEditValue(e.target.value)}
                    className="w-28"
                  />
                  <Button
                    size="sm"
                    onClick={async () => {
                      const num = feeEditValue.trim() === "" ? null : parseFloat(feeEditValue);
                      if (num !== null && (Number.isNaN(num) || num < 0)) return;
                      try {
                        await updateCleaningFee.mutateAsync({
                          taskId: task.id,
                          propertyId,
                          cleaningFee: num,
                        });
                        setFeeOverride({ taskId: task.id, value: num });
                        setFeeEditing(false);
                        setFeeEditValue("");
                      } catch {
                        // toast handled by mutation or caller
                      }
                    }}
                    disabled={updateCleaningFee.isPending}
                  >
                    Save
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setFeeEditing(false); setFeeEditValue(""); }}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="font-medium">
                    {displayFee != null && !Number.isNaN(displayFee) ? `$${Number(displayFee).toFixed(2)}` : "Not set"}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground"
                    onClick={() => {
                      setFeeEditing(true);
                      setFeeEditValue(displayFee != null ? String(displayFee) : "");
                    }}
                  >
                    {displayFee != null ? "Edit" : "Set rate"}
                  </Button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">Shown on the property Invoice (Billing tab). Owner pays payee directly.</p>
            </div>
          )}

          {/* Task Photos - always show when completed; show gallery or placeholder */}
          {task.status === "completed" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Camera className="w-4 h-4" />
                Task Photos
              </div>
              {submissionPhotos && submissionPhotos.length > 0 ? (
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {Array.from(new Set(submissionPhotos.map(p => `${p.room_name}::${p.task_text}`))).slice(0, 3).map(key => {
                    const [roomName, ...taskParts] = key.split("::");
                    const taskText = taskParts.join("::");
                    const beforePhotos = getPhotosForTask(submissionPhotos, roomName, taskText, "before")
                      .map(p => ({ id: p.id, photo_url: p.photo_url }));
                    const afterPhotos = getPhotosForTask(submissionPhotos, roomName, taskText, "after")
                      .map(p => ({ id: p.id, photo_url: p.photo_url }));
                    const refPhotos = getRefPhotosForTask(referencePhotos || [], roomName, taskText)
                      .map(p => ({ id: p.id, photo_url: p.photo_url }));
                    return (
                      <div key={key} className="p-2 bg-muted/30 rounded-lg border text-xs">
                        <SubmissionPhotoGallery
                          roomName={roomName}
                          taskText={taskText}
                          referencePhotos={refPhotos}
                          beforePhotos={beforePhotos}
                          afterPhotos={afterPhotos}
                        />
                      </div>
                    );
                  })}
                  {submissionPhotos.length > 3 && (
                    <p className="text-xs text-muted-foreground text-center">
                      +{submissionPhotos.length - 3} more photos in full report
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No task photos for this cleaning.</p>
              )}
            </div>
          )}

          {/* Proof evidence (reference vs proof per standard) */}
          {task.status === "completed" && submission && task.checklist_id && (
            <div className="space-y-3 pt-2 border-t">
              <ProofEvidenceSection
                submissionId={submission.id}
                checklistId={task.checklist_id}
                submittedAt={submission.submitted_at}
              />
            </div>
          )}

          {/* Rating - always show when completed and we have submission/task link */}
          {task.status === "completed" && cleaningTaskFromSubmission && (
            <CleanerRatingCard
              taskId={cleaningTaskFromSubmission.id}
              cleanerName={cleanerName ?? "Cleaner"}
              existingRating={cleaningTaskFromSubmission.rating}
              existingNote={cleaningTaskFromSubmission.rating_note}
              ratedAt={cleaningTaskFromSubmission.rated_at}
              canRate={
                cleaningTaskFromSubmission.checklist_completed === true
                && cleaningTaskFromSubmission.inventory_completed === true
                && cleaningTaskFromSubmission.rating === null
              }
            />
          )}

          {/* Edit Task + Import photos + Messages */}
          <div className="pt-4 border-t flex flex-col gap-2">
            {task.status !== "missed" && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setImportPhotosOpen(true)}
              >
                <Upload className="w-4 h-4 mr-2" />
                Import turnover photos
              </Button>
            )}
            {onEditTask && (task.status === "scheduled" || task.status === "in_progress") && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  onOpenChange(false);
                  onEditTask(task);
                }}
              >
                <Pencil className="w-4 h-4 mr-2" />
                Edit Task
              </Button>
            )}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setMessagesOpen(true)}
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Messages
            </Button>
          </div>

          {/* Share with guest (if completed) */}
          {task.status === "completed" && (
            <ShareWithGuest cleaningTaskId={task.id} propertyId={propertyId} />
          )}

          {/* View Report Link (if completed) */}
          {task.status === "completed" && (
            <div className="pt-4">
              <Link to={`/properties/${propertyId}?tab=reports`}>
                <Button variant="outline" className="w-full">
                  <FileText className="w-4 h-4 mr-2" />
                  View Cleaning Report
                  <ExternalLink className="w-3 h-3 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </SheetContent>
      
      {/* Message Thread Modal */}
      <MessageThreadModal
        open={messagesOpen}
        onOpenChange={setMessagesOpen}
        taskId={task.id}
        taskTitle={`Task ${formatScheduledDate(task.scheduled_date, "MMM d")}`}
      />

      <ImportTurnoverPhotosDialog
        open={importPhotosOpen}
        onOpenChange={setImportPhotosOpen}
        propertyId={propertyId}
        taskId={task.id}
        checklistId={task.checklist_id}
        existingSubmissionId={submission?.id}
        taskStatus={task.status}
      />
    </Sheet>
  );
};
